import { useMemo, useState } from 'react';
import { Maybe, QuestionItem, AnswersItem } from '__generated__/graphql';
import { useBackgroundAnswerSave } from '../hooks/useBackgroundAnswerSave';
import QuestionCard from './QuestionCard';

type MissingQuestionPagerProps = {
  questions: Maybe<QuestionItem>[];
  onComplete: (answersSubset: AnswersItem) => void;
};

export default function MissingQuestionPager({ questions, onComplete }: MissingQuestionPagerProps) {
  const sanitizedQuestions = useMemo(() => (questions || []).filter(Boolean) as QuestionItem[], [questions]);
  const total = sanitizedQuestions.length;

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [answersMap, setAnswersMap] = useState<Record<string, boolean | null>>({});
  const { saveAnswer } = useBackgroundAnswerSave();

  const currentQuestion = sanitizedQuestions[currentIndex];

  const handleAnswer = (value: boolean) => {
    if (!currentQuestion?.id) return;
    const nextAnswers = { ...answersMap, [currentQuestion.id]: value };
    setAnswersMap(nextAnswers);

    // background save as user answers
    saveAnswer(currentQuestion.id, value);

    const isLast = currentIndex >= total - 1;
    if (isLast) {
      // Prepare subset only for the questions in this missing flow
      const subset = Object.keys(nextAnswers).reduce((acc, k) => {
        acc[k as keyof AnswersItem] = nextAnswers[k] as boolean | null;
        return acc;
      }, {} as Partial<AnswersItem>) as AnswersItem;
      onComplete(subset);
      return;
    }
    setCurrentIndex((i) => Math.min(i + 1, total - 1));
  };

  if (total === 0) {
    // Nothing to show; parent should handle redirect
    return null;
  }

  return <QuestionCard question={currentQuestion} onAnswer={handleAnswer} />;
}
